import React, { useMemo, useState } from "react";

import { Stack, Paper, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import {
  useGetCurrentUser,
  useListUsers,
} from "../../../store";

import { User, UserType } from "../../../types";

import { userHasPermission } from "../../../types-helpers";
import { AdminUserFilter } from "../../../utils/contrast-color";
import BlockUserModal from "./block-user-modal";
import AdminUsersTable from "./admin-users-table";

type AdminUsersTabProps = {
  selectedClientId?: string;
};

const AdminUsersTab: React.FC<AdminUsersTabProps> = ({ selectedClientId }) => {

  const { data: currentUser } = useGetCurrentUser();

  const [toBlock, setToBlock] = useState<User | null>(null);

  const { data: users = [], isLoading: usersLoading } =
    useListUsers(true, selectedClientId);

  const [searchTerm, setSearchTerm] = React.useState("");
  const [cardFilter, setCardFilter] = React.useState<AdminUserFilter>("All");
  const [sortBy, setSortBy] = React.useState<string>("lastLogin");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage] = React.useState(10);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedClientId, cardFilter]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const getUserStatus = (user: User) => {
    if (user.userType === UserType.Blocked) return "Blocked";
    if (user.userType === UserType.Inactive) return "Inactive";
    return "Active";
  };

  const sortUsers = (users: User[]) => {
    return [...users].sort((a, b) => {
      let aValue: any = a[sortBy as keyof User];
      let bValue: any = b[sortBy as keyof User];

      if (sortBy === "lastLogin") {
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;

        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortBy === "fullName") {
        aValue = aValue?.toLowerCase() || "";
        bValue = bValue?.toLowerCase() || "";
      } else if (sortBy === "status") {
        aValue = getUserStatus(a);
        bValue = getUserStatus(b);
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || "";
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  const { userTypeStats } = useMemo(() => {
    const userTypeStats: Record<AdminUserFilter, User[]> = {
      [UserType.Blocked]: [],
      [UserType.Inactive]: [],
      [UserType.Guest]: [],
      [UserType.User]: [],
      [UserType.OrgAdmin]: [],
      [UserType.SysAdmin]: [],
      [UserType.DeveloperAdmin]: [],
      ["All"]: [],
      ["Active"]: [],
    };
    for (let user of users) {
      userTypeStats[user.userType].push(user);

      if (userHasPermission(UserType.Guest, user)) {
        userTypeStats["Active"].push(user);
      }

      userTypeStats["All"].push(user);
    }

    return {
      userTypeStats,
    };
  }, [users]);

  // Filter users based on search term and user type
  let filteredUsers = (userTypeStats[cardFilter] || [])?.filter(
    (user) =>
      user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Apply sorting
  filteredUsers = sortUsers(filteredUsers);

  return (
    <>
      <Stack spacing={2}>
        {/* Users Table with Tab-style Filters */}
        <Paper>
          {/* Filters */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 1 }}>
            <TextField
              placeholder="Search users by name or email..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>

          <AdminUsersTable
            usersLoading={usersLoading}
            filteredUsers={filteredUsers}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            searchTerm={searchTerm}
            currentUserId={currentUser?.userId || ""}
            selectedClientId={selectedClientId}
            onBlockClick={(user) => setToBlock(user)}
          />
        </Paper>
      </Stack>
      {toBlock && (
        <BlockUserModal
          open={toBlock != null}
          user={toBlock}
          onClose={() => setToBlock(null)}
        />
      )}
    </>
  );
};

export default AdminUsersTab;
